import React, { useState, useEffect, useContext } from 'react'
import { UserInfo, Organizationinfo, PermissionInfo } from '@/types'
import { useNavigate } from 'react-router-dom'
import { getUserInfo, getUserOrganizations, getUerPermissions, setDefaultOrg, getUserOAStatus } from '@/api'
import { useQuery, useMutation } from '@tanstack/react-query'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import { usePath } from '@/hooks'
import { isPublicPage } from '@/utils/booleans'
import { getPromotionInfo } from '@/api'

export interface AuthInfoProps {
  auth: boolean
  userInfo: UserInfo | null
  orgInfo: Organizationinfo | null
  permissions?: PermissionInfo[] | null
  verified: boolean
  loading: boolean
  loadingOrg: boolean
  hasOrg: boolean
  onBoarding?: boolean
  hasFollowService: boolean
  promotionInfo?: any
}

export interface AuthContextProps {
  authInfo: AuthInfoProps
  updateAuthInfo: (v: Partial<AuthInfoProps>) => void
}

export const AuthContext = React.createContext({} as AuthContextProps)

const Auth = ({ children }: { children: React.ReactElement | React.ReactElement[] }) => {
  const [authInfo, setAuthInfo] = useState<AuthInfoProps>({
    auth: false,
    userInfo: null,
    orgInfo: null,
    permissions: null,
    verified: false,
    loading: true,
    loadingOrg: true,
    hasOrg: true,
    hasFollowService: true,
    promotionInfo: null,
  })

  const updateAuthInfo = ({
    userInfo,
    orgInfo,
    auth,
    verified,
    permissions,
    loading,
    loadingOrg,
    hasOrg,
    hasFollowService,
    promotionInfo,
  }: {
    userInfo?: Partial<UserInfo>
    orgInfo?: Partial<Organizationinfo>
    permissions?: PermissionInfo[]
    auth?: boolean
    verified?: boolean
    loading?: boolean
    loadingOrg?: boolean
    hasOrg?: boolean
    hasFollowService?: boolean
    promotionInfo?: any
  }) => {
    // @ts-ignore
    setAuthInfo((authInfo) => ({
      ...authInfo,
      orgInfo: orgInfo ? { ...authInfo?.orgInfo, ...orgInfo } : authInfo?.orgInfo,
      userInfo: userInfo ? { ...authInfo?.userInfo, ...userInfo } : authInfo?.userInfo,
      permissions: permissions ? [...permissions] : authInfo?.permissions,
      promotionInfo: promotionInfo ? { ...authInfo?.promotionInfo, ...promotionInfo } : authInfo?.promotionInfo,
      auth: typeof auth === 'boolean' ? auth : authInfo.auth,
      verified: typeof verified === 'boolean' ? verified : authInfo.verified,
      loading: !!loading,
      loadingOrg: loadingOrg === null ? true : !!loadingOrg,
      hasOrg: hasOrg === null ? true : !!hasOrg,
      hasFollowService:
        hasFollowService === null || hasFollowService === undefined ? authInfo.hasFollowService : !!hasFollowService,
    }))
  }

  useEffect(() => {
    getPromotionInfo().then((res) => {
      if (res.referral_code) {
        updateAuthInfo({
          promotionInfo: res,
        })
      }
    })
  }, [])

  return (
    // @ts-ignore
    <AuthContext.Provider value={{ authInfo, updateAuthInfo }}>{children}</AuthContext.Provider>
  )
}

export const AuthComponent = ({ children, needOrg }: { children: React.ReactElement; needOrg?: boolean }) => {
  const { authInfo, updateAuthInfo } = useContext(AuthContext)
  const navigate = useNavigate()
  const { path } = usePath()
  const auth = authInfo?.auth
  const hasOrg = authInfo?.hasOrg
  const orgInfo = authInfo?.orgInfo

  const initUserOrgInfo = () => {
    return Promise.all([getUserInfo(), getUserOrganizations()])
  }

  const { data, isLoading: initLoading } = useQuery(['getAuthInitUserOrgData'], initUserOrgInfo, {
    onSuccess: ([user, orgData]) => {
      updateAuthInfo({
        auth: true,
        userInfo: user,
        verified: user?.is_certified === 1,
        loading: false,
        orgInfo: orgData?.data?.find((org) => org.id === user.org_id) || orgData?.data?.[0],
        loadingOrg: false,
        // @ts-ignore
        hasOrg: orgData?.count > 0,
      })
    },
    onError: () => {
      updateAuthInfo({
        auth: false,
        userInfo: null,
        verified: false,
        orgInfo: null,
        loading: false,
      })
    },
    refetchOnWindowFocus: true,
  })

  // const { isLoading: loadingProfile } = useQuery(["getUserInfo"], getUserInfo, {
  //   onSuccess: (result) => {
  //     updateAuthInfo({
  //       auth: true,
  //       userInfo: result,
  //       verified: result?.is_certified === 1,
  //       loading: false,
  //     });
  //   },
  //   onError: () => {
  //     updateAuthInfo({
  //       auth: false,
  //       userInfo: null,
  //       verified: false,
  //       orgInfo: null,
  //       loading: false,
  //     });
  //   },
  //   refetchOnWindowFocus: true,
  // });

  // const defaultOrg = useMutation(setDefaultOrg, {
  //   onSuccess: () => {
  //     // navigate(0);
  //   },
  //   onError: () => {
  //     alert.error('切换空间失败')
  //   },
  // })

  useQuery(['getUserOAStatus'], () => getUserOAStatus(), {
    onSuccess: (res) => {
      updateAuthInfo({
        hasFollowService: res.is_follow,
      })
    },
    onError: () => {
      updateAuthInfo({
        hasFollowService: true,
      })
    },
    enabled: !!authInfo?.auth,
  })

  // const { data: organizations, isLoading: loadingOrg } = useQuery(
  //   ["getUserOrganizations"],
  //   () => getUserOrganizations(),
  //   {
  //     refetchOnWindowFocus: true,
  //     refetchOnMount: true,
  //     onSuccess: (result) => {
  //       if (
  //         currentOrgId &&
  //         result?.data?.find((org) => org.id === currentOrgId)
  //       ) {
  //         updateAuthInfo({
  //           orgInfo: result?.data?.find((org) => org.id === currentOrgId),
  //           loadingOrg: false,
  //           hasOrg: result?.count > 0,
  //         });
  //       } else {
  //         updateAuthInfo({
  //           orgInfo: result?.data?.[0],
  //           loadingOrg: false,
  //           hasOrg: result?.count > 0,
  //         });
  //         defaultOrg.mutate({ ID: result?.data?.[0]?.id });
  //       }
  //     },
  //     onError: (err: string) => {
  //       alert.error(err ?? "获取组织信息失败");
  //       updateAuthInfo({
  //         orgInfo: null,
  //         loadingOrg: false,
  //       });
  //     },
  //     enabled: !!authInfo?.auth,
  //   }
  // );

  const { isLoading: loadingPermission } = useQuery(
    ['getUerPermissions', orgInfo?.id],
    // @ts-ignore
    () => getUerPermissions(orgInfo?.id),
    {
      refetchOnMount: true,
      enabled: !!orgInfo?.id,
      onSuccess: (res) => {
        updateAuthInfo({ permissions: res.data })
      },
    },
  )

  useEffect(() => {
    if (needOrg && data?.[1]?.count === 0 && !initLoading) {
      navigate(`/console/starter${location?.search}`)
    }
  }, [hasOrg, data, needOrg, initLoading, navigate])

  return (
    <>
      {children}
      {!['/', 'starter'].includes(path) && !isPublicPage(location?.pathname) && (
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={(initLoading || auth) && loadingPermission}
        >
          <CircularProgress color='inherit' />
        </Backdrop>
      )}
    </>
  )
}

export default Auth
